import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { XIcon } from '@phosphor-icons/react';
import deepEq from 'fast-deep-equal';

import { Breadcrumb } from 'components/Breadcrumb';
import { PageHeading } from 'components/PageHeading';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { AuditLogActionType } from 'src/types/enums';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { Table } from '../../../components/table/Table';
import { Input } from '../../../components/input/Input';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { Button } from '../../../components/button/Button';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { useSearchParam } from '../../../hooks/useSearchParam';
import { AUDIT_LOG_TABLE_HEADERS, mapAuditLogRow } from '../components/AuditLogsList';
import { getAuditLogs, type AuditLogsFilter } from '../models';

const PAGE_SIZE = 50;

const ACTION_TYPE_OPTIONS: ISimpleComboboxItem[] = Object.values(AuditLogActionType).map((actionType) => ({
  key: actionType,
  name: actionType,
}));

enum FilterType {
  ActionType = 'action-type',
  StartDate = 'start-date',
  EndDate = 'end-date',
}

export const AuditLogsPage = () => {
  const [_searchParams, setSearchParams] = useSearchParams();

  const [selectedActionType, setSelectedActionType] = useSearchParam<ISimpleComboboxItem | null>(
    FilterType.ActionType,
    null,
  );
  const [startDate, setStartDate] = useSearchParam(FilterType.StartDate, '');
  const [endDate, setEndDate] = useSearchParam(FilterType.EndDate, '');

  const getFiltersValue = (): AuditLogsFilter => ({
    actionType: (selectedActionType?.key as AuditLogActionType | undefined) ?? null,
    from: startDate || null,
    to: endDate || null,
  });

  const [filters, setFilters] = useState<AuditLogsFilter>(() => getFiltersValue());

  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      const newFilters = getFiltersValue();
      setFilters((prev) => (deepEq(prev, newFilters) ? prev : newFilters));
    }, 100);

    return () => clearTimeout(timeoutRef);
  }, [selectedActionType, startDate, endDate]);

  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getAuditLogs(variables.cursor, variables.take, filters);
    },
    [filters],
  );
  const page = usePagination({
    key: 'id',
    pageSize: PAGE_SIZE,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [filters]);

  const activeFilterCount = useMemo(
    () => [selectedActionType, startDate, endDate].filter(Boolean).length,
    [selectedActionType, startDate, endDate],
  );

  return (
    <>
      <PageHeader title="Audit Logs" />

      <ListPage
        heading={<PageHeading noMarginBottom leftSide={<Breadcrumb currentItem="Audit Logs" />} />}
        filters={
          <FilterPanel
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => setSearchParams({})}
              >
                Wis alle filters
              </Button>
            }
          >
            <FilterField label="Actie">
              <SimpleSelect
                selectedItem={selectedActionType}
                items={ACTION_TYPE_OPTIONS}
                isOptional={true}
                onSelect={(val) => {
                  if (val?.key === selectedActionType?.key) {
                    setSelectedActionType(null);
                  } else {
                    setSelectedActionType(val);
                  }
                }}
              />
            </FilterField>

            <FilterField label="Van">
              <Input type="date" value={startDate} onChange={setStartDate} />
            </FilterField>

            <FilterField label="Tot">
              <Input type="date" value={endDate} onChange={setEndDate} />
            </FilterField>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={AUDIT_LOG_TABLE_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          emptyState={<span className="text-sm text-fg-muted">Geen audit logs gevonden</span>}
          mapData={mapAuditLogRow}
        />
      </ListPage>
    </>
  );
};
